import {
  PlainLink,
  Table as BaseTable,
  TableMenu,
} from '@/components_deprecated'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import type { AbsenceNode, AbsenceTypeNode } from '../types.graphql'
import {
  AbsenceStatusCell,
  AbsenceTypeCell,
  AbsenceTypeHeader,
  DateCell,
} from './table-cells'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useAbsenceHandler } from './handler.hooks'
import truncate from 'lodash/truncate'
import { isMobileOnly } from 'react-device-detect'
import { dateFromStringWithoutTimezone } from '../util'

const Table = styled((props: React.ComponentProps<typeof BaseTable>) => (
  <BaseTable {...props} />
))`
  position: static;

  ${props => props.theme.media.largePad}
  ${props => props.theme.media.mobile} {
    overflow: auto;
  }

  td.absence-info {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 12px 1rem;
    height: 56px;

    span {
      color: ${props => props.theme.colors.gray5};
      font-weight: 600;
    }
  }
`

interface AbsencesTableProps {
  absences: AbsenceNode[]
  loading: boolean
  showEmployee?: boolean
  selectedAbsenceType: Pick<AbsenceTypeNode, 'id' | 'name'> | null
  onSelectAbsenceType: (
    absenceType: Pick<AbsenceTypeNode, 'id' | 'name'> | null
  ) => void
}

export const AbsencesTable: React.FC<AbsencesTableProps> = ({
  absences,
  loading,
  showEmployee = false,
  selectedAbsenceType,
  onSelectAbsenceType,
}) => {
  const translations = useTranslate({
    dayStart: 'common.day-start',
    dayEnd: 'common.day-end',
    absenceType: 'timesheets.absence-type',
    status: 'common.status',
    reason: 'common.reason',
    employee: 'common.employee',
    noResults: 'common.no-results',

    open: 'common.open',
    link: 'common.link',
  })

  const absenceHandler = useAbsenceHandler()

  const tableHeaders = (
    <tr>
      <th>{translations.link}</th>
      {showEmployee && <th>{translations.employee}</th>}
      <th>{translations.dayStart}</th>
      <th>{translations.dayEnd}</th>
      {!isMobileOnly && (
        <>
          <AbsenceTypeHeader
            absenceType={selectedAbsenceType}
            text={translations.absenceType}
            onRemove={() => onSelectAbsenceType(null)}
          />
          <th>{translations.reason}</th>
        </>
      )}
      <th>{translations.status}</th>
      {/* Extra table header for menu */}
      <th></th>
    </tr>
  )

  const absenceRows = useMemo(() => {
    return absences.map(absence => {
      // TODO: Can this ever by null? Logically it can, but the old typing said it couldn't,
      // which was probably a mistake in the type.
      // Should this be handled another way?
      if (!absence.absenceType) return null

      const dayStart = dateFromStringWithoutTimezone(absence.datetimeStart)
      const dayEnd = dateFromStringWithoutTimezone(absence.datetimeEnd)
      const menuItems = absenceHandler.createAbsenceMenuItems(absence, true)

      return (
        <tr key={absence.id}>
          <td width="100px">
            <PlainLink
              to={`/absences/${absence.id}`}
              target="_blank"
              $color="iconSchemes.oneDrive"
              $hoverUnderline
            >
              {translations.open}
            </PlainLink>
          </td>
          {showEmployee && <td width="20px">{absence.user.fullName}</td>}

          <DateCell date={dayStart} side="start" type="absence" />

          <DateCell date={dayEnd} side="end" type="absence" />

          {!isMobileOnly && (
            <>
              <AbsenceTypeCell
                absenceType={absence.absenceType}
                isSelected={!!selectedAbsenceType}
                onSelect={onSelectAbsenceType}
              />

              <td className="absence-info">
                {truncate(absence.reason, { length: 60 })}
              </td>
            </>
          )}
          <AbsenceStatusCell requestStatus={absence.requestStatus} />
          <TableMenu
            items={menuItems}
            absenceLink={`/absences/${absence.id}`}
          />
        </tr>
      )
    })
  }, [
    absenceHandler,
    absences,
    onSelectAbsenceType,
    selectedAbsenceType,
    showEmployee,
    translations.open,
  ])

  return (
    <Table
      loading={loading}
      noData={absences.length === 0}
      noDataText={translations.noResults}
    >
      <thead>{tableHeaders}</thead>
      <tbody>{absenceRows}</tbody>
    </Table>
  )
}
